/**
 * Organization Settings Page (System)
 * Company structure and settings
 */
import React from 'react';
import { 
  Building2, 
  MapPin, 
  Globe,
  Users,
  Calendar,
  Edit,
  Save
} from 'lucide-react';

const ORG_INFO = {
  name: 'Acme Corporation',
  industry: 'Manufacturing',
  headquarters: 'Berlin, Germany',
  employees: '5,000+',
  fiscalYearEnd: 'December 31',
  reportingCurrency: 'EUR',
  subsidiaries: 12,
  countries: 8,
};

const ENTITIES = [
  { name: 'Acme Germany GmbH', type: 'Subsidiary', country: 'Germany', status: 'active' },
  { name: 'Acme France SAS', type: 'Subsidiary', country: 'France', status: 'active' },
  { name: 'Acme UK Ltd', type: 'Subsidiary', country: 'United Kingdom', status: 'active' },
  { name: 'Acme USA Inc', type: 'Subsidiary', country: 'United States', status: 'active' },
];

export default function OrganizationSettingsPage() {
  return (
    <div className="space-y-6" data-testid="organization-settings-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Organization Settings</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Manage company structure and organizational information
          </p>
        </div>
        <button 
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
          data-testid="save-org-btn"
        >
          <Save size={16} />
          <span className="text-sm font-medium">Save Changes</span>
        </button>
      </div>

      {/* Company Info */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-base font-semibold text-white">Company Information</h3>
          <button className="p-2 text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F] rounded-lg transition-colors">
            <Edit size={14} />
          </button>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Company Name</label>
            <p className="text-sm font-medium text-white mt-1">{ORG_INFO.name}</p>
          </div>
          <div>
            <label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Industry</label>
            <p className="text-sm font-medium text-white mt-1">{ORG_INFO.industry}</p>
          </div>
          <div>
            <label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Headquarters</label>
            <p className="text-sm font-medium text-white mt-1 flex items-center gap-1">
              <MapPin size={12} className="text-[#6B6B6B]" />
              {ORG_INFO.headquarters}
            </p>
          </div>
          <div>
            <label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Employees</label>
            <p className="text-sm font-medium text-white mt-1 flex items-center gap-1">
              <Users size={12} className="text-[#6B6B6B]" />
              {ORG_INFO.employees}
            </p>
          </div>
          <div>
            <label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Fiscal Year End</label>
            <p className="text-sm font-medium text-white mt-1 flex items-center gap-1">
              <Calendar size={12} className="text-[#6B6B6B]" />
              {ORG_INFO.fiscalYearEnd}
            </p>
          </div>
          <div>
            <label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Reporting Currency</label>
            <p className="text-sm font-medium text-white mt-1">{ORG_INFO.reportingCurrency}</p>
          </div>
          <div>
            <label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Subsidiaries</label>
            <p className="text-sm font-medium text-white mt-1">{ORG_INFO.subsidiaries}</p>
          </div>
          <div>
            <label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Countries</label>
            <p className="text-sm font-medium text-white mt-1 flex items-center gap-1">
              <Globe size={12} className="text-[#6B6B6B]" />
              {ORG_INFO.countries}
            </p>
          </div>
        </div>
      </div>

      {/* Entity Structure */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-white">Entity Structure</h3>
          <button className="text-xs text-emerald-400 hover:underline">Add Entity</button>
        </div>
        
        <div className="space-y-3">
          {ENTITIES.map((entity, idx) => (
            <div 
              key={idx} 
              className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg"
            >
              <div className="flex items-center gap-3">
                <Building2 size={16} className="text-[#6B6B6B]" />
                <div>
                  <p className="text-sm font-medium text-white">{entity.name}</p>
                  <p className="text-xs text-[#6B6B6B]">{entity.country}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-2 py-1 text-xs bg-[#1F1F1F] text-[#A0A0A0] rounded">
                  {entity.type}
                </span>
                <span className="px-2 py-1 text-xs bg-emerald-500/20 text-emerald-400 rounded">
                  {entity.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reporting Boundaries */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
        <h3 className="text-base font-semibold text-white mb-4">Reporting Boundaries</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-[#0A0A0A] rounded-lg">
            <h4 className="text-sm font-medium text-white mb-2">Financial Control</h4>
            <p className="text-xs text-[#6B6B6B]">Entities where the organization has control over financial and operating policies</p>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-2xl font-bold text-emerald-400">12</span>
              <span className="text-xs text-[#6B6B6B]">entities included</span>
            </div>
          </div>
          <div className="p-4 bg-[#0A0A0A] rounded-lg">
            <h4 className="text-sm font-medium text-white mb-2">Operational Control</h4>
            <p className="text-xs text-[#6B6B6B]">Entities where the organization has full authority to introduce operating policies</p>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-2xl font-bold text-blue-400">10</span>
              <span className="text-xs text-[#6B6B6B]">entities included</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
